import * as _ from 'lodash';
import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../complex/time-recording-entry.model';
import { TimesheetEntryInterface } from '../complex/timesheet-entry.interface';
import { TimesheetEntryModel } from '../complex/timesheet-entry.model';
import {
    JsonTimesheetManualEntryModel,
    TimesheetManualEntryModel,
    TimesheetManualEntryModelFromJson,
} from '../complex/timesheet-manual-entry.model';
import { ExpenseModel, ExpenseModelFromJson, JsonExpenseModel } from './expense.model';
import {
    JsonTimesheetNotificationModel,
    TimesheetNotificationModel,
    TimesheetNotificationModelFromJson,
} from './timesheet-notification.model';
import { TimesheetAndExpensesService } from '../../services/timesheet/timesheet-and-expenses.service';

export class TimesheetModel {
    public constructor(
        public startOfWork: moment.Moment,
        public notifications: TimesheetNotificationModel[],
        public additionalExpenses: ExpenseModel[],
        public manualTimesheetEntries: TimesheetManualEntryModel[],
        public endOfWork: moment.Moment,
        public isSubmitted: boolean,
        public attendanceType = ConfigConstants.AttendanceType.FREE,
        public startAddressValue = '',
        public endAddressValue = '',
        // utc timestamp from Date.now() or moment().valueOf() when TimesheetModel is written to
        // storage (indexed db or concurrent usage store)
        public saveTimestamp = 0,
    ) {}

    /**
     * Get all entries in the timesheet, regardless of origin (both timerecording and manually created entries are contained)
     *
     * @returns {TimesheetEntryInterface[]} all entries in the timesheet
     */
    public getTimesheetEntries(): TimesheetEntryInterface[] {
        // get all timerecording entries from the current timesheet
        let timesheetEntries = <TimesheetEntryInterface[]>_.chain(this.notifications)
            .map((timesheetNotification) => {
                if (timesheetNotification.isConfirmationSubmitted) {
                    timesheetNotification.timesheetEntries.forEach((entry) => {
                        // set all Travel Back entries to non-editable so that only the "last" travel back entry can be set to editable
                        if (TimesheetAndExpensesService.isTravelBackEntry(entry)) {
                            entry.isTravelBackEditable = false;
                        } else if (TimesheetAndExpensesService.isTravelToEntry(entry)) {
                            // set all Travel To entries to non-editable so that only the "first" travel to entry can be set to editable
                            entry.isTravelToEditable = false;
                        }
                    });
                    return timesheetNotification.timesheetEntries;
                } else {
                    return [];
                }
            })
            .flatten()
            .value();

        // merge with manual entries (those that close gaps)
        timesheetEntries = timesheetEntries.concat(this.manualTimesheetEntries);

        timesheetEntries = TimesheetModel.sortTimesheetEntries(timesheetEntries);

        // find the first travel To entry and set it to editable
        const firstTravelToEntry = timesheetEntries.find(
            (entry) => entry instanceof TimesheetEntryModel && TimesheetAndExpensesService.isTravelToEntry(entry),
        );
        if (firstTravelToEntry) {
            (<TimesheetEntryModel>firstTravelToEntry).isTravelToEditable = true;
        }

        // find last notification timesheet entry and if it is a travel back then set it to editable
        // it is possible that last entry is a phone fix, in that case it shouldn't be editable
        const lastEntry = <TimesheetEntryModel>(
            timesheetEntries.findLast((entry) => entry instanceof TimesheetEntryModel)
        );
        if (lastEntry && TimesheetAndExpensesService.isTravelBackEntry(lastEntry)) {
            lastEntry.isTravelBackEditable = true;
        }
        return timesheetEntries;
    }

    public getLastTravelBackEntryWithoutAdditionalTravelBack(): TimesheetEntryModel | undefined {
        const timesheetEntries = this.getTimesheetEntries();
        // find last travel back entry and set it to editable
        const lastTravelBackEntry = timesheetEntries.findLast(
            (entry) =>
                entry instanceof TimesheetEntryModel && entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK,
        );
        if (lastTravelBackEntry) {
            const additionalTravelBack = timesheetEntries.find(
                (entry) =>
                    entry instanceof TimesheetManualEntryModel &&
                    entry.taskTypeDropdown.selectedEntry.model.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK &&
                    entry.notificationId.equalsTo((<TimesheetEntryModel>lastTravelBackEntry).notificationId),
            );
            return !additionalTravelBack ? <TimesheetEntryModel>lastTravelBackEntry : undefined;
        }
        return undefined;
    }

    /**
     * This will give you the end-of-work from a timesheet.
     * We don't have an actual end-of-work entry anymore. Instead we return the last timestamp in the timesheet.
     *
     * @returns {moment.Moment} end-of-work time of the timesheet
     */
    public getEndOfWork(): moment.Moment {
        let endOfWork: moment.Moment;

        const timesheetEntries = this.getTimesheetEntries();

        // if there are entries in the timesheet, return the end time of the last value
        if (timesheetEntries.length > 0) {
            endOfWork = timesheetEntries[timesheetEntries.length - 1].endTime.value.clone();
        } else if (this.startOfWork) {
            // otherwise return the start-of-work time of the timesheet (if already set)
            endOfWork = this.startOfWork.clone();
        } else {
            // if the timesheet is completely empty, return null
            endOfWork = moment.invalid();
        }
        return endOfWork;
    }

    public static sortTimesheetEntries(entries: TimesheetEntryInterface[]): TimesheetEntryInterface[] {
        return entries.sort((entry1: TimesheetEntryInterface, entry2: TimesheetEntryInterface) => {
            // first check if the entries are correct in themselves
            // i.e. both should have a non negative duration before any sorting can be applied
            if (
                entry1.startDate.value.isAfter(entry1.endDate.value) ||
                entry2.startDate.value.isAfter(entry2.endDate.value)
            ) {
                return 0;
            } else if (
                entry1.startDate.value.isBefore(entry2.startDate.value) &&
                entry1.endDate.value.isSameOrBefore(entry2.endDate.value)
            ) {
                return -1;
            } else if (
                entry1.startDate.value.isAfter(entry2.startDate.value) &&
                entry1.endDate.value.isSameOrAfter(entry2.endDate.value)
            ) {
                return 1;
            } else {
                if (entry1.endDate.value.isBefore(entry2.endDate.value)) {
                    return -1;
                } else if (entry1.endDate.value.isSame(entry2.endDate.value)) {
                    return 0;
                } else {
                    return 1;
                }
            }
        });
    }
}

export class TimesheetModelFromJson extends TimesheetModel {
    public constructor(data: JsonTimesheetModel) {
        super(
            data.startOfWork ? moment(data.startOfWork) : moment.invalid(),
            data.notifications
                ? data.notifications.map((notification) => new TimesheetNotificationModelFromJson(notification))
                : [],
            data.additionalExpenses ? data.additionalExpenses.map((expense) => new ExpenseModelFromJson(expense)) : [],
            data.manualTimesheetEntries
                ? data.manualTimesheetEntries.map(
                      (timesheetEntry) => new TimesheetManualEntryModelFromJson(timesheetEntry),
                  )
                : [],
            data.endOfWork ? moment(data.endOfWork) : moment.invalid(),
            data.isSubmitted ? data.isSubmitted : false,
            data.attendanceType,
            // TODO: Remove ternary operator condition check after v2.42.0 goes live
            // Migration logic for v2.42.0: check for undefined if the timesheet json does not contain these fields
            data.startAddressValue ? data.startAddressValue : '',
            data.endAddressValue ? data.endAddressValue : '',
            data.saveTimestamp ?? 0,
        );
    }
}

export interface JsonTimesheetModel {
    startOfWork: string;
    notifications: JsonTimesheetNotificationModel[];
    additionalExpenses: JsonExpenseModel[];
    manualTimesheetEntries: JsonTimesheetManualEntryModel[];
    endOfWork: string;
    isSubmitted: boolean;
    attendanceType: ConfigConstants.AttendanceType;
    startAddressValue: string;
    endAddressValue: string;
    saveTimestamp: number;
}
